import { useEffect, useMemo, useState } from 'react'

import { api } from '../../api/client'
import type { EagleMonitorato } from '../../api/types'
import { t } from '../../i18n/lingua'

/** A parameter as the picker hands it back: enough to label a series without a second page. */
export interface ParametroScelto {
  key: string
  name: string
  kind: string
  /** For an interface: the port description (ifAlias), the IP and the state, «·»-separated. */
  detail: string
  unit: string
}

/**
 * The choice «which CI, which parameter» for the widgets and the reports that
 * draw an Eagle parameter: a search box over the monitored CIs, then the
 * parameters of the chosen one. An interface shows its **port description**
 * next to the name («Gi1/0/1 — Uplink DC · 10.1.1.1 · up»): the comment the
 * network team wrote on the switch is what tells ports apart, and asking to
 * keep a second page open to read it is not a user interface.
 */
export function SceltaCIParametro({ ciId, chiave, onCambia, soloTipi }: {
  ciId: string
  chiave: string
  onCambia: (ciId: string, key: string, ciName: string, parametro?: ParametroScelto) => void
  /** Only the parameters of these kinds (the Link widget wants interfaces). */
  soloTipi?: string[]
}) {
  const [cis, setCis] = useState<EagleMonitorato[]>([])
  const [parametri, setParametri] = useState<ParametroScelto[]>([])
  const [cerca, setCerca] = useState('')
  useEffect(() => {
    let vivo = true
    void api.eagleCis().then((d) => { if (vivo) setCis(d.cis) }).catch(() => undefined)
    return () => { vivo = false }
  }, [])
  useEffect(() => {
    if (!ciId) { setParametri([]); return }
    let vivo = true
    void api.ciEagle(ciId)
      .then((d) => {
        if (!vivo) return
        setParametri((d.params ?? [])
          .filter((p) => p.enabled && (!soloTipi || soloTipi.includes(p.kind)))
          .map((p) => ({ key: p.key, name: p.name, kind: p.kind, detail: p.detail ?? '', unit: p.unit ?? '' })))
      })
      .catch(() => { if (vivo) setParametri([]) })
    return () => { vivo = false }
  }, [ciId, soloTipi])
  const nome = (id: string) => cis.find((c) => c.ci_id === id)?.name ?? ''
  const filtro = cerca.trim().toLowerCase()
  const visibili = useMemo(() => cis.filter((c) =>
    !filtro || c.ci_id === ciId
    || [c.name, c.mgmt_ip ?? '', c.ci_class ?? ''].some((x) => x.toLowerCase().includes(filtro))), [cis, filtro, ciId])

  return (
    <>
      <label className="field span-3">
        <span className="field__label">{t('CI')}</span>
        <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder={t('cerca per nome, indirizzo o classe…')} aria-label={t('Cerca un CI')} />
        <select value={ciId} onChange={(e) => onCambia(e.target.value, '', nome(e.target.value))} size={Math.min(6, Math.max(2, visibili.length + 1))}>
          <option value="">—</option>
          {visibili.map((c) => <option key={c.ci_id} value={c.ci_id}>{c.name}{c.mgmt_ip ? ` (${c.mgmt_ip})` : ''}</option>)}
        </select>
        {filtro && <span className="field__hint">{t('{n} CI su {m}', { n: visibili.length, m: cis.length })}</span>}
      </label>
      <label className="field span-3">
        <span className="field__label">{t('Parametro')}</span>
        <select value={chiave} disabled={!ciId} size={Math.min(6, Math.max(2, parametri.length + 1))}
                onChange={(e) => onCambia(ciId, e.target.value, nome(ciId), parametri.find((p) => p.key === e.target.value))}>
          <option value="">—</option>
          {parametri.map((p) => (
            <option key={p.key} value={p.key}>{p.name || p.key}{p.detail ? ` — ${p.detail}` : ''}{p.unit ? ` [${p.unit}]` : ''}</option>
          ))}
        </select>
        {ciId && parametri.length === 0 && <span className="field__hint">{t('Nessun parametro acceso su questo CI.')}</span>}
      </label>
    </>
  )
}
